import { GripVertical, Trash2 } from "lucide-react";
import { formatPriceCents } from "@/components/services/service-meta";
import { cn } from "@/lib/cn";
import { formatarQuantidade } from "@/lib/quantidade";

const inputCls =
  "h-8 w-full rounded border border-zinc-200 bg-white px-2 text-right text-xs tabular-nums text-zinc-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground";

export type LinhaDaFicha = {
  /** Um dos dois, nunca os dois — a escolha É a regra (ver TechnicalSheetLine). */
  materialLineId: string | null;
  materialCategoryId: string | null;
  rotulo: string;
  unidade: string | null;
  precoCents: number | null;
  indireto: boolean;
  quantidade: string;
  fator: string;
};

const paraNumero = (v: string) => Number((v || "0").replace(",", "."));

/**
 * Uma linha da ficha: material, quantidade líquida, fator de perda e o custo que sai disso.
 *
 * Mostra a quantidade BRUTA calculada ao lado, porque é ela que vira dinheiro — e ver "0,1 vira
 * 0,11" é o que faz o fator de perda deixar de ser um número abstrato.
 */
export function FichaTecnicaLinha({
  linha,
  onChange,
  onRemover,
}: {
  linha: LinhaDaFicha;
  onChange: (l: LinhaDaFicha) => void;
  onRemover: () => void;
}) {
  const bruta = paraNumero(linha.quantidade) * (paraNumero(linha.fator) || 1);
  const emAberto = !!linha.materialCategoryId;
  const custoCents = !emAberto && linha.precoCents != null ? bruta * linha.precoCents : null;

  return (
    <tr className="border-b border-zinc-100 last:border-b-0 dark:border-border/50">
      <td className="py-1.5 pr-2">
        <span className="flex items-center gap-1.5">
          <GripVertical className="h-3 w-3 shrink-0 text-zinc-300 dark:text-border" />
          <span className="min-w-0">
            <span className="block truncate text-xs text-zinc-900 dark:text-foreground">{linha.rotulo}</span>
            <span className="flex gap-1.5 text-[10px] text-zinc-400 dark:text-muted-foreground/70">
              {emAberto && <span className="font-semibold text-amber-600 dark:text-amber-400">Escolher na produção</span>}
              {linha.indireto && <span>Indireto</span>}
              {linha.precoCents != null && !emAberto && (
                <span>
                  {formatPriceCents(linha.precoCents)}
                  {linha.unidade ? `/${linha.unidade}` : ""}
                </span>
              )}
              {linha.precoCents == null && !emAberto && <span className="text-amber-600 dark:text-amber-400">Sem preço</span>}
            </span>
          </span>
        </span>
      </td>
      <td className="py-1.5 pr-1.5">
        <input
          value={linha.quantidade}
          onChange={(e) => onChange({ ...linha, quantidade: e.target.value })}
          inputMode="decimal"
          aria-label={`Quantidade de ${linha.rotulo}`}
          className={inputCls}
        />
      </td>
      <td className="py-1.5 pr-1.5">
        <input
          value={linha.fator}
          onChange={(e) => onChange({ ...linha, fator: e.target.value })}
          inputMode="decimal"
          aria-label={`Fator de perda de ${linha.rotulo}`}
          className={inputCls}
        />
      </td>
      <td className="py-1.5 pr-2 text-right text-[11px] tabular-nums text-zinc-500 dark:text-muted-foreground">
        {bruta > 0 ? formatarQuantidade(bruta) : "—"}
      </td>
      <td
        className={cn(
          "py-1.5 pr-2 text-right text-xs tabular-nums",
          linha.indireto ? "text-zinc-400 line-through dark:text-muted-foreground/60" : "text-zinc-900 dark:text-foreground",
        )}
      >
        {emAberto ? "—" : custoCents != null ? formatPriceCents(Math.round(custoCents)) : "—"}
      </td>
      <td className="py-1.5 text-right">
        <button
          type="button"
          onClick={onRemover}
          aria-label={`Remover ${linha.rotulo}`}
          className="rounded p-1 text-zinc-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30 dark:hover:text-red-400"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </td>
    </tr>
  );
}
